import type { BaseModel } from "@/models/base.model"
import type { IBase } from "@/interfaces/api/base.interface.ts"

/**
 * Represents a field that may be lazily loaded.
 * When populated, it holds the full model instance.
 * When not yet loaded, it holds a string (typically the ID).
 */
// At the type level this is simply T — the compiler treats it as fully populated.
// At runtime it may be a string (ID reference) if not yet loaded.
export type LazyField<T extends IBase> = T

/**
 * Type guard to check if a lazy field has been populated with the full object.
 */
export function isPopulated<T extends BaseModel>(
  field: LazyField<T> | undefined | null
): field is T {
  return field != null && typeof field !== "string"
}

/**
 * Extracts the ID from a lazy field.
 * If populated, returns the object's id. If a string reference, returns the string itself.
 */
export function lazyFieldId<T extends IBase>(
  field: LazyField<T> | undefined | null
): string | undefined {
  if (field == null) return undefined
  return typeof field === "string" ? field : field.id
}
